import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getCurrentTenantUserId } from '../../api/client';
import { useNotifications } from '../../context/NotificationContext';
import './ConversationList.css';
import { translateError } from '../../utils/errorMessages';

interface Participant {
  _id: string;
  name: string;
  email: string;
}

interface Group {
  _id: string;
  name: string;
}

interface Conversation {
  _id: string;
  type: 'direct' | 'group';
  participants: Participant[];
  group?: Group;
  name?: string;
  lastMessage: {
    content: string;
    sender: string;
    timestamp: string;
  } | null;
  updatedAt: string;
}

interface ConversationListProps {
  onSelectConversation: (conversation: Conversation) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({ onSelectConversation }) => {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { onNewMessage } = useNotifications();

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await api.messages.getConversations();

      setConversations(data as Conversation[]);
      setLoading(false);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Listen for new messages to update conversation list
  useEffect(() => {
    const unsubscribe = onNewMessage((message, conversationUpdate) => {
      setConversations(prev => {
        const index = prev.findIndex(c => c._id === conversationUpdate._id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            lastMessage: conversationUpdate.lastMessage,
            updatedAt: new Date().toISOString(),
          };
          // Move to top
          const [conv] = updated.splice(index, 1);
          return [conv, ...updated];
        }
        return prev;
      });
    });

    return unsubscribe;
  }, [onNewMessage]);

  // Tenant user id — participant._id is a tenant users.id, not the global id.
  const getOtherParticipant = (participants: Participant[]): Participant | undefined => {
    const userId = getCurrentTenantUserId();
    return participants.find(p => String(p._id) !== userId);
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return t('messaging.yesterday');
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getConversationDisplay = (conversation: Conversation) => {
    if (conversation.type === 'group') {
      return {
        name: conversation.name || conversation.group?.name || t('messaging.groupChat'),
        isGroup: true,
        participantCount: conversation.participants.length,
      };
    } else {
      const other = getOtherParticipant(conversation.participants);
      return {
        name: other?.name || t('messaging.unknownUser'),
        initial: other?.name.charAt(0).toUpperCase() || '?',
        isGroup: false,
      };
    }
  };

  if (loading) {
    return (
      <div className="conversation-list__loading">
        <div className="conversation-list__spinner"></div>
        <span>{t('messaging.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversation-list__error">
        <span>{error}</span>
        <button onClick={fetchConversations}>{t('common.retry')}</button>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="conversation-list__empty">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
          <path fillRule="evenodd" d="M4.804 21.644A6.707 6.707 0 006 21.75a6.721 6.721 0 003.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 01-.814 1.686.75.75 0 00.44 1.223zM8.25 10.875a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25zM10.875 12a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0zm4.875-1.125a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25z" clipRule="evenodd" />
        </svg>
        <p>{t('messaging.noConversations')}</p>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      {conversations.map(conversation => {
        const display = getConversationDisplay(conversation);
        return (
          <button
            key={conversation._id}
            className="conversation-list__item"
            onClick={() => onSelectConversation(conversation)}
          >
            {display.isGroup ? (
              <div className="conversation-list__avatar conversation-list__avatar--group">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                  <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM15.75 9.75a3 3 0 116 0 3 3 0 01-6 0zM2.25 9.75a3 3 0 116 0 3 3 0 01-6 0zM6.31 15.117A6.745 6.745 0 0112 12a6.745 6.745 0 016.709 7.498.75.75 0 01-.372.568A12.696 12.696 0 0112 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 01-.372-.568 6.787 6.787 0 011.019-4.38z" clipRule="evenodd" />
                  <path d="M5.082 14.254a8.287 8.287 0 00-1.308 5.135 9.687 9.687 0 01-1.764-.44l-.115-.04a.563.563 0 01-.373-.487l-.01-.121a3.75 3.75 0 013.57-4.047zM20.226 19.389a8.287 8.287 0 00-1.308-5.135 3.75 3.75 0 013.57 4.047l-.01.121a.563.563 0 01-.373.486l-.115.04c-.567.2-1.156.349-1.764.441z" />
                </svg>
              </div>
            ) : (
              <div className="conversation-list__avatar">
                {display.initial}
              </div>
            )}
            <div className="conversation-list__content">
              <div className="conversation-list__header">
                <span className="conversation-list__name">
                  {display.name}
                  {display.isGroup && display.participantCount && (
                    <span className="conversation-list__participant-count">
                      ({display.participantCount})
                    </span>
                  )}
                </span>
                {conversation.lastMessage && (
                  <span className="conversation-list__time">
                    {formatTime(conversation.lastMessage.timestamp)}
                  </span>
                )}
              </div>
              <p className="conversation-list__preview">
                {conversation.lastMessage?.content || t('messaging.noMessages')}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ConversationList;
